import {
  useAllCategoriesQuery,
  useAllCategoryGroupsQuery,
  useBindingsQuery,
  useCashFlowByCategoryByMonthQuery,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import * as React from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {CategoryFilterInput} from 'src/CategoryFilterInput';
import {FilterDropdown} from 'src/components/FilterDropdown';
import {useDayKey} from 'src/lib/useDayKey';
import {cn} from 'src/lib/utils';
import Loading from 'src/Loading';
import {buildTrendsModel} from 'src/TrendsView/trendsModel';
import type {
  TrendsBand,
  TrendsChip,
  TrendsSeries,
} from 'src/TrendsView/trendsModel';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// The app's data starts in 2012; a year with nothing simply draws empty.
const FIRST_YEAR = 2012;

const formatCompact = (cents: number): string => {
  const dollars = cents / 100;
  if (Math.abs(dollars) >= 100_000) {
    return `$${Math.round(dollars / 1000)}k`;
  }
  if (Math.abs(dollars) >= 10_000) {
    return `$${(dollars / 1000).toFixed(1)}k`;
  }
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};

const formatExact = (cents: number): string =>
  (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const seriesColor = (series: TrendsSeries): string => {
  if (series.color != null) {
    return `var(--color-${series.color}-500)`;
  }
  if (series.paletteIndex < 0) {
    return series.dashed ? 'var(--muted-foreground)' : 'var(--chart-3)';
  }
  return `var(--chart-${(series.paletteIndex % 8) + 1})`;
};

const bandColor = (band: TrendsBand): string =>
  band.color != null
    ? `var(--color-${band.color}-500)`
    : `var(--chart-${(band.paletteIndex % 8) + 1})`;

/** What the tooltip needs to draw a row for one series. */
type SeriesMeta = {label: string; stroke: string; dashed: boolean};

type TooltipEntry = {dataKey?: string | number; value?: number | string};
type TrendsTooltipProps = {
  active?: boolean;
  label?: string;
  payload?: TooltipEntry[];
  metaBySeries: Map<string, SeriesMeta>;
};

/**
 * Custom hover card: a small line swatch — solid or dotted, in the
 * series' color — beside neutral-colored text, so the comparison reads
 * from the swatches instead of a wall of tinted numbers.
 */
const TrendsTooltip = ({
  active,
  label,
  payload,
  metaBySeries,
}: TrendsTooltipProps) => {
  if (active !== true || payload == null || payload.length === 0) {
    return null;
  }
  return (
    <div className="border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-md">
      <div className="text-muted-foreground mb-1 font-medium">{label}</div>
      <div className="flex flex-col gap-1">
        {payload.flatMap((entry) => {
          const meta =
            typeof entry.dataKey === 'string'
              ? metaBySeries.get(entry.dataKey)
              : undefined;
          if (meta == null || typeof entry.value !== 'number') {
            return [];
          }
          return [
            <div
              key={String(entry.dataKey)}
              className="flex items-center gap-2"
            >
              <svg width="18" height="4" aria-hidden="true">
                <line
                  x1="1"
                  y1="2"
                  x2="17"
                  y2="2"
                  stroke={meta.stroke}
                  strokeWidth="2.5"
                  strokeDasharray={meta.dashed ? '3 3' : undefined}
                />
              </svg>
              <span className="text-muted-foreground">{meta.label}</span>
              <span className="text-foreground ml-auto pl-4 font-medium tabular-nums">
                {formatExact(entry.value)}
              </span>
            </div>,
          ];
        })}
      </div>
    </div>
  );
};

/**
 * Per-point dot renderer: draws the series' end marker — a dot at its
 * last drawn point, plus the year label when the chart is sparse enough.
 * Recharts calls this for every point; other points draw nothing.
 */
const endMarkerDot = (
  series: TrendsSeries,
  stroke: string,
  lastIndex: number,
) =>
  function EndMarkerDot(props: {cx?: number; cy?: number; index?: number}) {
    const {cx, cy, index} = props;
    if (
      series.endMarker === 'none' ||
      index !== lastIndex ||
      cx == null ||
      cy == null
    ) {
      return <g key={`m-${series.id}-${String(index)}`} />;
    }
    return (
      <g key={`m-${series.id}-${String(index)}`}>
        <circle cx={cx} cy={cy} r={3.5} fill={stroke} />
        {series.endMarker === 'dot-label' && (
          <text
            x={cx + 8}
            y={cy + 4}
            fontSize={12}
            fontWeight={600}
            fill={stroke}
          >
            {series.endLabel}
          </text>
        )}
      </g>
    );
  };

const lastDrawnIndex = (points: (number | null)[]): number => {
  for (let i = points.length - 1; i >= 0; i--) {
    if (points[i] != null) {
      return i;
    }
  }
  return -1;
};

const chipDotColor = (chip: TrendsChip): string =>
  chip.color != null
    ? `var(--color-${chip.color}-500)`
    : `var(--chart-${(chip.paletteIndex % 8) + 1})`;

type ChipRowProps = {
  chips: TrendsChip[];
  selected: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
  allLabel: string;
  formatValue: (cents: number) => string;
};

const ChipRow = ({
  chips,
  selected,
  onToggle,
  onClear,
  allLabel,
  formatValue,
}: ChipRowProps) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <button
      type="button"
      onClick={onClear}
      className={cn(
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        selected.length === 0
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border text-muted-foreground hover:text-foreground',
      )}
    >
      {allLabel}
    </button>
    {chips.map((chip) => {
      const active = selected.includes(chip.id);
      return (
        <button
          key={chip.id}
          type="button"
          onClick={() => onToggle(chip.id)}
          className={cn(
            'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
            active
              ? 'border-primary text-foreground'
              : 'border-border text-muted-foreground hover:text-foreground',
          )}
        >
          <span
            className="size-2 rounded-full"
            style={{background: chipDotColor(chip)}}
          />
          {chip.label}
          <span className="text-muted-foreground font-normal">
            {formatValue(chip.baseTotal)}
          </span>
        </button>
      );
    })}
  </div>
);

/**
 * The Trends tab: monthly (non-cumulative) spending, this year as solid
 * lines against last year dashed. Group chips isolate or compare; with one
 * group isolated, its member categories get their own chip row.
 */
export const TrendsView = () => {
  // Focus-refreshed so a tab left mounted across a month boundary picks
  // up the newly completed month.
  const dayKey = useDayKey();
  const now = parseDate(dayKey);
  const currentYear = now.year;
  // Null means "the current year": the default advances across New Year
  // by construction, while an explicitly picked year sticks.
  const [selectedYear, setSelectedYear] = React.useState<number | null>(null);
  const baseYear = selectedYear ?? currentYear;
  const [filterCategories, setFilterCategories] = React.useState<
    string[] | null
  >(null);
  const [selectedGroupIds, setSelectedGroupIds] = React.useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = React.useState<
    string[]
  >([]);
  const [viewMode, setViewMode] = React.useState<'lines' | 'stacked'>('lines');
  const [selectedMonth, setSelectedMonth] = React.useState<number | null>(null);

  const {
    data: categoriesData,
    loading: categoriesLoading,
    error: categoriesError,
  } = useAllCategoriesQuery({fetchPolicy: 'cache-and-network'});
  const {
    data: groupsData,
    loading: groupsLoading,
    error: groupsError,
  } = useAllCategoryGroupsQuery({fetchPolicy: 'cache-and-network'});
  const {
    data: bindingsData,
    loading: bindingsLoading,
    error: bindingsError,
  } = useBindingsQuery({fetchPolicy: 'cache-and-network'});
  const {
    data: seriesData,
    loading: seriesLoading,
    error: seriesError,
  } = useCashFlowByCategoryByMonthQuery({
    variables: {
      // Three years: the pair, plus the echo year that fills the base
      // year's unlived months from the frontier.
      startDate: `${baseYear - 2}-01-01`,
      endDate: `${baseYear}-12-31`,
    },
    fetchPolicy: 'cache-and-network',
  });

  const categoriesById = React.useMemo(
    () =>
      new Map(
        (categoriesData?.allCategories ?? []).map((category) => [
          category._id,
          {name: category.name},
        ]),
      ),
    [categoriesData],
  );

  // Dangling binding (category deleted since) reads as unset.
  const taxesCategoryId = React.useMemo(() => {
    const id =
      bindingsData?.bindings.find((binding) => binding.key === 'taxesCategory')
        ?.entityId ?? null;
    return id != null && categoriesById.has(id) ? id : null;
  }, [bindingsData, categoriesById]);

  const model = React.useMemo(
    () =>
      buildTrendsModel({
        rows: (seriesData?.cashFlowByCategoryByMonth ?? []).map((row) => ({
          key: row.key ?? null,
          month: row.month,
          net: row.net,
        })),
        categoriesById,
        categoryGroups: (groupsData?.allCategoryGroups ?? []).map((group) => ({
          _id: group._id,
          name: group.name,
          categoryIds: group.categoryIds,
          color: group.color ?? null,
        })),
        filterCategoryIds: filterCategories,
        baseYear,
        // The in-progress month plots as a misleading plunge (and skews
        // the caption's like-months comparison), so the base year ends at
        // the last complete month — except in January, when the partial
        // month is all there is.
        baseYearLastMonth:
          baseYear === currentYear ? Math.max(0, now.month - 2) : 11,
        selectedGroupIds,
        selectedCategoryIds,
        taxesCategoryId,
      }),
    [
      seriesData,
      categoriesById,
      groupsData,
      filterCategories,
      baseYear,
      currentYear,
      now.month,
      selectedGroupIds,
      selectedCategoryIds,
      taxesCategoryId,
    ],
  );

  const chartData = React.useMemo(
    () =>
      MONTH_LABELS.map((label, index) => {
        const row: Record<string, string | number | null> = {month: label};
        for (const series of model.series) {
          row[series.id] = series.points[index];
        }
        return row;
      }),
    [model],
  );

  const stackData = React.useMemo(
    () =>
      MONTH_LABELS.map((label, index) => {
        const row: Record<string, string | number | null> = {month: label};
        for (const band of model.stackBands) {
          row[band.id] = band.points[index];
        }
        row[model.stackPrevTotal.id] = model.stackPrevTotal.points[index];
        return row;
      }),
    [model],
  );

  const metaBySeries = React.useMemo(() => {
    const meta = new Map<string, SeriesMeta>();
    for (const series of model.series) {
      meta.set(series.id, {
        label: series.label,
        stroke: seriesColor(series),
        dashed: series.dashed,
      });
    }
    for (const band of model.stackBands) {
      meta.set(band.id, {
        label: `${band.label} · ${baseYear}`,
        stroke: bandColor(band),
        dashed: false,
      });
    }
    meta.set(model.stackPrevTotal.id, {
      label: model.stackPrevTotal.label,
      stroke: 'var(--muted-foreground)',
      dashed: true,
    });
    return meta;
  }, [model, baseYear]);

  const toggleGroup = (id: string) => {
    setSelectedCategoryIds([]);
    setSelectedGroupIds((current) =>
      current.includes(id)
        ? current.filter((g) => g !== id)
        : [...current, id],
    );
  };

  const error = categoriesError ?? groupsError ?? bindingsError ?? seriesError;
  // The metadata queries gate rendering too: with groups still absent the
  // model would pool every category under "Ungrouped", and with
  // categories absent member chips would read "Unknown category".
  const loading =
    (categoriesLoading && categoriesData == null) ||
    (groupsLoading && groupsData == null) ||
    (bindingsLoading && bindingsData == null) ||
    (seriesLoading && seriesData == null);
  const delta =
    model.totals.prev > 0
      ? (model.totals.base - model.totals.prev) / model.totals.prev
      : null;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-foreground text-xl font-semibold">Spending</h1>
          <p className="text-muted-foreground text-sm">
            Each month&rsquo;s spending, {baseYear} solid against{' '}
            {baseYear - 1} dashed. Inflows inside a spending category reduce
            that category instead of counting as income.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {(['lines', 'stacked'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium capitalize transition-colors',
                viewMode === mode
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground',
              )}
            >
              {mode}
            </button>
          ))}
          <FilterDropdown
            label="Year"
            allLabel={String(currentYear)}
            triggerLabel={`${baseYear} vs ${baseYear - 1}`}
            options={Array.from(
              {length: currentYear - FIRST_YEAR + 1},
              (_v, i) => {
                const year = currentYear - i;
                return {key: String(year), label: String(year)};
              },
            )}
            value={String(baseYear)}
            onChange={(value) =>
              // The clear row reports 'all' and means "back to the
              // current year" — stored as null so it keeps advancing.
              setSelectedYear(value === 'all' ? null : Number(value))
            }
          />
          <CategoryFilterInput
            categories={filterCategories}
            setCategories={setFilterCategories}
          />
        </div>
      </div>

      {error != null && (
        <div className="text-destructive py-12 text-center text-sm">
          Couldn&rsquo;t load spending: {error.message}
        </div>
      )}
      {error == null && loading && <Loading />}
      {error == null && !loading && (
        <>
          <div className="h-[380px] w-full">
            <ResponsiveContainer>
              {viewMode === 'lines' ? (
                <LineChart
                  data={chartData}
                  margin={{top: 8, right: 52, bottom: 0, left: 8}}
                  onClick={(state) => {
                    const index = MONTH_LABELS.indexOf(
                      String(state?.activeLabel ?? ''),
                    );
                    if (index >= 0) {
                      setSelectedMonth((current) =>
                        current === index ? null : index,
                      );
                    }
                  }}
                >
                  <CartesianGrid
                    stroke="var(--border)"
                    strokeDasharray="3 3"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{fill: 'var(--muted-foreground)', fontSize: 12}}
                    tickLine={false}
                    axisLine={{stroke: 'var(--border)'}}
                  />
                  <YAxis
                    tickFormatter={(value: number) => formatCompact(value)}
                    tick={{fill: 'var(--muted-foreground)', fontSize: 12}}
                    tickLine={false}
                    axisLine={false}
                    width={64}
                  />
                  <Tooltip
                    content={<TrendsTooltip metaBySeries={metaBySeries} />}
                  />
                  {model.series.map((series) => {
                    const stroke = seriesColor(series);
                    // The echo year fades further and dashes tighter than
                    // the comparison year, so the chart's right half reads
                    // as two distinct histories, not a doubled line.
                    const echo = series.id.endsWith(':echo');
                    return (
                      <Line
                        key={series.id}
                        dataKey={series.id}
                        stroke={stroke}
                        strokeWidth={series.dashed ? 1.5 : 2}
                        strokeDasharray={
                          echo ? '2 4' : series.dashed ? '5 4' : undefined
                        }
                        strokeOpacity={echo ? 0.45 : series.dashed ? 0.65 : 1}
                        dot={endMarkerDot(
                          series,
                          stroke,
                          lastDrawnIndex(series.points),
                        )}
                        activeDot={{r: 3}}
                        isAnimationActive={false}
                      />
                    );
                  })}
                </LineChart>
              ) : (
                <ComposedChart
                  data={stackData}
                  margin={{top: 8, right: 52, bottom: 0, left: 8}}
                  onClick={(state) => {
                    const index = MONTH_LABELS.indexOf(
                      String(state?.activeLabel ?? ''),
                    );
                    if (index >= 0) {
                      setSelectedMonth((current) =>
                        current === index ? null : index,
                      );
                    }
                  }}
                >
                  <CartesianGrid
                    stroke="var(--border)"
                    strokeDasharray="3 3"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{fill: 'var(--muted-foreground)', fontSize: 12}}
                    tickLine={false}
                    axisLine={{stroke: 'var(--border)'}}
                  />
                  <YAxis
                    tickFormatter={(value: number) => formatCompact(value)}
                    tick={{fill: 'var(--muted-foreground)', fontSize: 12}}
                    tickLine={false}
                    axisLine={false}
                    width={64}
                  />
                  <Tooltip
                    content={<TrendsTooltip metaBySeries={metaBySeries} />}
                  />
                  {model.stackBands.map((band) => (
                    <Area
                      key={band.id}
                      dataKey={band.id}
                      stackId="base"
                      stroke={bandColor(band)}
                      fill={bandColor(band)}
                      fillOpacity={0.45}
                      strokeWidth={1}
                      isAnimationActive={false}
                    />
                  ))}
                  <Line
                    dataKey={model.stackPrevTotal.id}
                    stroke="var(--muted-foreground)"
                    strokeWidth={1.5}
                    strokeDasharray="5 4"
                    dot={endMarkerDot(
                      model.stackPrevTotal,
                      'var(--muted-foreground)',
                      lastDrawnIndex(model.stackPrevTotal.points),
                    )}
                    activeDot={{r: 3}}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              )}
            </ResponsiveContainer>
          </div>

          <ChipRow
            chips={model.chips}
            selected={model.activeGroupIds}
            onToggle={toggleGroup}
            onClear={() => {
              setSelectedGroupIds([]);
              setSelectedCategoryIds([]);
            }}
            allLabel="All spending"
            formatValue={formatCompact}
          />
          {model.categoryChips.length > 0 && (
            <ChipRow
              chips={model.categoryChips}
              selected={model.activeCategoryIds}
              onToggle={(id) =>
                setSelectedCategoryIds((current) =>
                  current.includes(id)
                    ? current.filter((c) => c !== id)
                    : [...current, id],
                )
              }
              onClear={() => setSelectedCategoryIds([])}
              allLabel="Whole group"
              formatValue={formatCompact}
            />
          )}

          {selectedMonth != null && (
            <div className="border-border rounded-lg border p-3">
              <div className="mb-2 flex items-baseline justify-between">
                <span className="text-foreground text-sm font-semibold">
                  {MONTH_LABELS[selectedMonth]} {baseYear} · what moved
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedMonth(null)}
                  className="text-muted-foreground hover:text-foreground text-xs"
                >
                  Close
                </button>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th className="py-1 font-medium">Category</th>
                    <th className="py-1 text-right font-medium">
                      {MONTH_LABELS[selectedMonth]} {baseYear}
                    </th>
                    <th className="py-1 text-right font-medium">
                      {MONTH_LABELS[selectedMonth]} {baseYear - 1}
                    </th>
                    <th className="py-1 text-right font-medium">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {model.monthDetails[selectedMonth].slice(0, 8).map((row) => (
                    <tr
                      key={row.key ?? '__uncategorized'}
                      className="border-border border-t"
                    >
                      <td className="text-foreground py-1">{row.label}</td>
                      <td className="py-1 text-right tabular-nums">
                        {row.base == null ? '—' : formatExact(row.base)}
                      </td>
                      <td className="text-muted-foreground py-1 text-right tabular-nums">
                        {formatExact(row.prev)}
                      </td>
                      <td
                        className={cn(
                          'py-1 text-right font-medium tabular-nums',
                          row.delta != null && row.delta > 0
                            ? 'text-destructive'
                            : 'text-muted-foreground',
                        )}
                      >
                        {row.delta == null
                          ? '—'
                          : `${row.delta > 0 ? '+' : ''}${formatExact(row.delta)}`}
                      </td>
                    </tr>
                  ))}
                  {model.monthDetails[selectedMonth].length === 0 && (
                    <tr className="border-border border-t">
                      <td
                        colSpan={4}
                        className="text-muted-foreground py-2 text-center"
                      >
                        Nothing in this month.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {model.monthDetails[selectedMonth].length > 8 && (
                <div className="text-muted-foreground mt-1 text-right text-[11px]">
                  {model.monthDetails[selectedMonth].length - 8} smaller
                  changes not shown
                </div>
              )}
            </div>
          )}

          <p className="text-muted-foreground text-xs">
            Jan–{MONTH_LABELS[model.totals.months - 1]}:{' '}
            {formatExact(model.totals.base)} vs{' '}
            {formatExact(model.totals.prev)} in {baseYear - 1}
            {delta != null && (
              <>
                {' '}
                (
                <span
                  className={
                    delta > 0 ? 'text-destructive' : 'text-foreground'
                  }
                >
                  {delta > 0 ? '+' : ''}
                  {Math.round(delta * 100)}%
                </span>
                )
              </>
            )}
            {model.taxesChipId != null &&
              model.activeGroupIds.length === 0 &&
              ` — excluding ${
                categoriesById.get(model.taxesChipId)?.name ?? 'Taxes'
              }`}
            . Click a group to isolate it, several to compare, or a month to
            see what moved.
          </p>
        </>
      )}
    </div>
  );
};
